import { useEffect, useState } from 'react';

import Switch from '@components/switch/Switch';
import UserPermissionTypes from '@constants/permissions';
import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import useCheckPermission from '@hooks/checkPermission';
import useNotify from '@hooks/notify';
import { getLocalStorageItem } from '@utils/storage';

import { LeadOrgConfig } from './types';

import {
  useGetLeadOrgConfigQuery,
  useUpdateLeadOrgConfigMutation
} from '../../queries/LeadManagementQuery';

const DEFAULT_CONFIG: LeadOrgConfig = {
  is_lead_assignment_allowed_for_agent: false,
  is_lead_deletion_allowed_for_sales_agent: false
};

const TOGGLE_CONFIG: {
  field: keyof LeadOrgConfig;
  label: string;
  testId: string;
}[] = [
  {
    field: 'is_lead_assignment_allowed_for_agent',
    label: 'Allow Agents to re-assign leads',
    testId: 'agent-reassignment'
  },
  {
    field: 'is_lead_deletion_allowed_for_sales_agent',
    label: 'Allow Agents to delete leads',
    testId: 'agent-delete'
  }
];

const AgentReassignmentToggle = () => {
  const organizationId = getLocalStorageItem(
    LOCAL_STORAGE_KEYS.ORGANIZATION_ID
  );
  const { createErrorAlert } = useNotify();
  const { checkPermission } = useCheckPermission();

  const hasConfigView = checkPermission([
    UserPermissionTypes.LEAD_MANAGEMENT_CONFIG_VIEW
  ]);
  const hasConfigEdit = checkPermission([
    UserPermissionTypes.LEAD_MANAGEMENT_CONFIG_EDIT
  ]);

  const { data: orgConfig } = useGetLeadOrgConfigQuery(
    { organizationId: organizationId || '' },
    { skip: !organizationId || !hasConfigView }
  );

  const [updateOrgConfig, { isLoading: isUpdating }] =
    useUpdateLeadOrgConfigMutation();

  const [config, setConfig] = useState<LeadOrgConfig>(DEFAULT_CONFIG);
  const [activeField, setActiveField] = useState<keyof LeadOrgConfig | null>(
    null
  );

  const isToggleDisabled = !hasConfigEdit || isUpdating;
  const inactiveToggleClassName = (field: keyof LeadOrgConfig) =>
    isUpdating && activeField !== field ? '!opacity-100' : undefined;

  const handleConfigToggle = async (
    field: keyof LeadOrgConfig,
    value: boolean
  ) => {
    const previousConfig = config;
    const nextConfig = { ...config, [field]: value };
    setConfig(nextConfig);
    setActiveField(field);
    try {
      await updateOrgConfig({
        organizationId: organizationId || '',
        body: nextConfig
      }).unwrap();
    } catch {
      setConfig(previousConfig);
      createErrorAlert('Failed to update setting. Please try again.');
    }
  };

  useEffect(() => {
    if (orgConfig) {
      setConfig(
        TOGGLE_CONFIG.reduce(
          (acc, { field }) => ({
            ...acc,
            [field]: orgConfig[field] ?? false
          }),
          {} as LeadOrgConfig
        )
      );
    }
  }, [orgConfig]);

  if (!hasConfigView) return null;

  return (
    <div className="flex flex-col rounded-xl bg-white">
      <div className="border-b border-cofeeGreyBlue5 px-6 py-4 text-lg font-semibold text-cofeeNeutral10">
        Permissions
      </div>
      {TOGGLE_CONFIG.map(({ field, label, testId }) => (
        <div key={field} className="flex items-center gap-2 p-6">
          <Switch
            value={config[field]}
            handleClick={(value) => handleConfigToggle(field, value)}
            disabled={isToggleDisabled}
            buttonClassName={inactiveToggleClassName(field)}
            testId={testId}
          />
          <div className="text-base font-medium text-cofeeNeutral13">
            {label}
          </div>
        </div>
      ))}
    </div>
  );
};

export default AgentReassignmentToggle;
